import React, {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {Helmet} from 'react-helmet'
import {useAuth} from '../modules/auth/core/Auth'
import { toAbsoluteUrl } from '_metronic/helpers'
import { doc, getDoc, getFirestore, updateDoc } from 'firebase/firestore'

interface RegistrationLoadingProps {
  userName?: string
  onComplete?: () => void
}

export const RegistrationLoading: React.FC<RegistrationLoadingProps> = ({
  userName = 'User',
  onComplete,
}) => {
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState(0)
  const navigate = useNavigate()
  const {currentUser, auth, setCurrentUser} = useAuth()

  const steps = [
    'Setting up your workspace...',
    'Configuring security controls...',
    'Initializing compliance frameworks...',
    'Preparing your dashboard...',
    'Finalizing setup...',
  ]

  useEffect(() => {
    if (!auth) {
      window.location.href = '/auth/login'
      return
    }
    
    if (currentUser && !currentUser.isNewClient) {
      window.location.href = '/dashboard'
      return
    }

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer)
          setTimeout(async () => {
            try {
              const db = getFirestore()
              const userDocRef = doc(db, 'users', auth.uid || '')
              const userDocSnap = await getDoc(userDocRef)
              
              if (userDocSnap.exists()) {
                const userData = userDocSnap.data() as any
                const updatedUserData = {
                  ...userData,
                  isNewClient: false
                }
                setCurrentUser(updatedUserData)
                
                await updateDoc(userDocRef, { 
                  isNewClient: false 
                })
                
                setTimeout(() => {
                  // if (onComplete) {
                  //   onComplete()
                  // } else {
                    window.location.href = '/dashboard'
                  // }
                }, 200)
              } else {
                window.location.href = '/auth/login'
              }
            } catch (error) {
              window.location.href = '/auth/login'
            }
          }, 500)
          return 100
        }
        return prev + 1 
      })
    }, 100)

    const stepTimer = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev < steps.length - 1) {
          return prev + 1
        }
        return prev
      })
    }, 3000)

    return () => {
      clearInterval(timer)
      clearInterval(stepTimer)
    }
  }, [navigate, onComplete, steps.length, currentUser, auth, setCurrentUser])

  return (
    <>
      <Helmet>
        <title>Setting up SecureSlate...</title>
      </Helmet>
      <div className='d-flex flex-column flex-root'>
        <div className='d-flex flex-column flex-center min-vh-100 bg-body'>
          <div className='text-center'>
            <div className='mb-11'>
              <img
                src='/Secureslate_logos/Secureslate_dark.png'
                alt='SecureSlate'
                className='h-30px'
              />
            </div>

            <h1 className='text-dark fw-bold mb-8 fs-1'>
              Setting up your account...
            </h1>

            <div className='mb-12 position-relative' style={{height: '200px'}}>
            <img
            className='mx-auto w-275px w-md-50 w-xl-350px mb-10 mb-lg-20 d-none d-sm-block'
            src={toAbsoluteUrl('/media/svg/illustrations/iso-27001-headerimage.svg')}
            alt=''
          />
            </div>

            <div className='mb-8 mt-4'>
              <div className='d-flex align-items-center mb-3'>
                <div className='flex-grow-1 bg-secondary rounded h-5px me-2'>
                  <div
                    className='bg-primary rounded h-5px transition-all duration-300'
                    style={{width: `${progress}%`}}
                  ></div>
                </div>
                <span className='text-gray-700 fs-7'>{progress}%</span>
              </div>
            </div>

            <div className='mb-8'>
              <p className='text-gray-800 fw-semibold fs-6'>{steps[currentStep]}</p>
            </div>

            <div className='text-gray-700 fs-7'>
              <p className='mb-2'>Thanks for signing up! Our team is configuring your compliance account.</p>
              <p>This won't take more than a minute. You'll be taken to your dashboard once it's ready.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
